'use client';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useState, type ReactNode } from 'react';

/**
 * Hydration-aware mount flag so SSR markup is a plain visible <div> and
 * the motion variant is only opted into post-hydration. Prevents FOIC
 * (flash of invisible content) and keeps the landing crawlable / no-JS
 * friendly. The `set-state-in-effect` rule complains because we flip
 * state immediately on mount with no dep — intentional, this is the
 * canonical post-mount handshake.
 */
function useIsHydrated() {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional post-mount flip to opt into client-only motion variant
    setHydrated(true);
  }, []);
  return hydrated;
}

/**
 * Subtle entrance animation — fade + slight Y translate. Editorial pacing
 * (Aesop / Linear / Resend), not bouncy or springy. Default fires on mount;
 * `whileInView` triggers once when scrolled into the viewport (and stays
 * visible — `once: true`).
 *
 * SSR-safe: renders a plain <div> on the server and on first client paint;
 * post-hydration the motion variant takes over. Respects
 * `prefers-reduced-motion` via `useReducedMotion()` — when reduced, content
 * just renders with no animation.
 */
export function FadeIn({
  children,
  y = 8,
  duration = 0.5,
  delay = 0,
  className,
  whileInView = false,
}: {
  children: ReactNode;
  y?: number;
  duration?: number;
  delay?: number;
  className?: string;
  whileInView?: boolean;
}) {
  const hydrated = useIsHydrated();
  const reduce = useReducedMotion();
  const animate = hydrated && !reduce;

  if (!animate) return <div className={className}>{children}</div>;

  const motionProps = whileInView
    ? {
        initial: { opacity: 0, y },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
      }
    : {
        initial: { opacity: 0, y },
        animate: { opacity: 1, y: 0 },
      };

  return (
    <motion.div className={className} {...motionProps} transition={{ duration, delay, ease: 'easeOut' }}>
      {children}
    </motion.div>
  );
}
